"use client"

import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"

export interface CalendarProps {
  mode?: "single" | "range"
  selected?: Date | { from: Date; to?: Date }
  onSelect?: (date: Date | { from: Date; to?: Date } | undefined) => void
  disabled?: (date: Date) => boolean
  className?: string
  showOutsideDays?: boolean
}

interface CalendarState {
  currentMonth: Date
  selectedDate?: Date
  selectedRange?: { from: Date; to?: Date }
}

export function Calendar({
  mode = "single",
  selected,
  onSelect,
  disabled,
  className = "",
  showOutsideDays = true,
}: CalendarProps) {
  const [state, setState] = React.useState<CalendarState>(() => ({
    currentMonth: new Date(),
    selectedDate: mode === "single" && selected instanceof Date ? selected : undefined,
    selectedRange: mode === "range" && selected && typeof selected === "object" && 'from' in selected ? selected : undefined,
  }))

  const today = new Date()
  const currentDate = new Date()

  // Get days in month
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    const days: (Date | null)[] = []

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      if (showOutsideDays) {
        const prevMonthDay = new Date(year, month, -startingDayOfWeek + i + 1)
        days.push(prevMonthDay)
      } else {
        days.push(null)
      }
    }

    // Add days of the current month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day))
    }

    // Add empty cells for days after the last day of the month
    const remainingCells = 42 - days.length // 6 rows × 7 days
    for (let i = 1; i <= remainingCells; i++) {
      if (showOutsideDays) {
        const nextMonthDay = new Date(year, month + 1, i)
        days.push(nextMonthDay)
      } else {
        days.push(null)
      }
    }

    return days
  }

  const navigateMonth = (direction: "prev" | "next") => {
    setState(prev => ({
      ...prev,
      currentMonth: new Date(
        prev.currentMonth.getFullYear(),
        prev.currentMonth.getMonth() + (direction === "next" ? 1 : -1),
        1
      )
    }))
  }

  const handleDateClick = (date: Date) => {
    if (disabled && disabled(date)) return

    if (mode === "single") {
      setState(prev => ({ ...prev, selectedDate: date }))
      onSelect?.(date)
    } else if (mode === "range") {
      setState(prev => {
        const currentRange = prev.selectedRange

        if (!currentRange?.from || (currentRange.from && currentRange.to)) {
          // Start new range - clear any existing range and start fresh
          const newRange = { from: date, to: undefined }
          // Call onSelect immediately for the new range start
          setTimeout(() => onSelect?.({ from: date, to: undefined }), 0)
          return { ...prev, selectedRange: newRange }
        } else {
          // Complete range - we have a 'from' date but no 'to' date
          const newRange = {
            from: currentRange.from,
            to: date >= currentRange.from ? date : currentRange.from
          }
          if (date < currentRange.from) {
            newRange.from = date
            newRange.to = currentRange.from
          }
          // Call onSelect immediately for the completed range
          setTimeout(() => onSelect?.(newRange), 0)
          return { ...prev, selectedRange: newRange }
        }
      })
    }
  }

  const isDateSelected = (date: Date) => {
    if (mode === "single") {
      return state.selectedDate &&
             date.getDate() === state.selectedDate.getDate() &&
             date.getMonth() === state.selectedDate.getMonth() &&
             date.getFullYear() === state.selectedDate.getFullYear()
    } else if (mode === "range" && state.selectedRange) {
      const { from, to } = state.selectedRange
      if (from && to) {
        return date >= from && date <= to
      } else if (from) {
        return date.getTime() === from.getTime()
      }
    }
    return false
  }

  const isDateInRange = (date: Date) => {
    if (mode === "range" && state.selectedRange?.from && state.selectedRange?.to) {
      return date > state.selectedRange.from && date < state.selectedRange.to
    }
    return false
  }

  const isToday = (date: Date) => {
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear()
  }

  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === state.currentMonth.getMonth() &&
           date.getFullYear() === state.currentMonth.getFullYear()
  }

  const days = getDaysInMonth(state.currentMonth)
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ]
  const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]

  return (
    <div className={`p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            navigateMonth("prev")
          }}
          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
        >
          <ChevronLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
        </button>

        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
          {monthNames[state.currentMonth.getMonth()]} {state.currentMonth.getFullYear()}
        </h2>

        <button
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            navigateMonth("next")
          }}
          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
        >
          <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
        </button>
      </div>

      {/* Day names */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map(day => (
          <div key={day} className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((date, index) => {
          if (!date) {
            return <div key={index} className="h-8" />
          }

          const isDisabled = disabled && disabled(date)
          const isSelected = isDateSelected(date)
          const isInRange = isDateInRange(date)
          const isTodayDate = isToday(date)
          const isCurrentMonthDate = isCurrentMonth(date)

          return (
            <button
              key={index}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleDateClick(date)
              }}
              disabled={isDisabled}
              className={`
                h-8 w-8 text-xs rounded-md transition-colors
                ${isSelected
                  ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                  : isInRange
                  ? 'bg-gray-100 dark:bg-gray-700'
                  : isTodayDate
                  ? 'bg-gray-200 dark:bg-gray-600 font-semibold'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                }
                ${!isCurrentMonthDate ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'}
                ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {date.getDate()}
            </button>
          )
        })}
      </div>
    </div>
  )
}
